import { SEARCHABLE_FIELDS, type SearchableField } from "./schema";

export interface ParsedQuery {
  q: string;
  queryBy: SearchableField[];
  filterBy?: string;
  sortBy?: string;
}

export interface FilterSelection {
  field: "author" | "categories" | "traits" | "keywords";
  values: string[];
  operator: "and" | "or";
}

export const SEARCH_SORTS = [
  "relevance",
  "title_asc",
  "title_desc",
  "author_asc",
  "author_desc",
  "source_order",
  "most_viewed",
  "most_favorited",
] as const;

export type SearchSort = (typeof SEARCH_SORTS)[number];

export interface SearchRequest {
  query: string;
  fields: SearchableField[];
  filters: FilterSelection[];
  sort: SearchSort;
  page: number;
  perPage: number;
  /** Restrict results to these logical Essence ids (used by the Favorites view). */
  ids?: string[];
}

const FIELD_FILTER_RE =
  /\b(author|title|ai_summary|description|effects_text|sections_text):("[^"]+"|\S+)/g;

function escapeFilterValue(value: string): string {
  return value.replace(/([\\:])/g, "\\$1");
}

function buildFilterBy(filters: FilterSelection[]): string | undefined {
  const groups: string[] = [];
  for (const f of filters) {
    if (f.values.length === 0) continue;
    const field = f.field;
    const values = f.values.map((v) => `${field}:=${escapeFilterValue(v)}`);
    const group = f.operator === "and" ? `(${values.join(" && ")})` : `(${values.join(" || ")})`;
    groups.push(group);
  }
  return groups.length > 0 ? groups.join(" && ") : undefined;
}

function buildIdFilter(ids: string[]): string {
  const escaped = ids.map((id) => `\`${id.replace(/`/g, "")}\``);
  return `essence_id:=[${escaped.join(",")}]`;
}

export function parseSearchRequest(request: SearchRequest): ParsedQuery {
  let query = request.query.trim();
  const selectedFields = request.fields.length > 0 ? request.fields : [...SEARCHABLE_FIELDS];

  // Extract field-specific text filters from the query string.
  const fieldFilters = new Map<SearchableField, string[]>();
  query = query.replace(FIELD_FILTER_RE, (_match, field: SearchableField, value) => {
    const clean = value.startsWith('"') ? value.slice(1, -1) : value;
    const list = fieldFilters.get(field) ?? [];
    list.push(clean);
    fieldFilters.set(field, list);
    return "";
  });

  // If the user typed field-specific terms, prefer searching those fields.
  let queryBy = selectedFields;
  if (fieldFilters.size > 0 && request.fields.length === 0) {
    queryBy = Array.from(fieldFilters.keys());
  }

  // Append field-specific terms to the main query so they still participate in ranking.
  for (const [, values] of fieldFilters) {
    for (const value of values) {
      query += ` "${value}"`;
    }
  }

  query = query.trim();
  if (!query) query = "*";

  const filterByParts: string[] = [];

  // Filter selections (faceted metadata).
  const metadataFilter = buildFilterBy(request.filters);
  if (metadataFilter) filterByParts.push(metadataFilter);

  // Explicit id scoping (favorites).
  if (request.ids && request.ids.length > 0) {
    filterByParts.push(buildIdFilter(request.ids));
  }

  // Latest-version-only is the default search behavior.
  filterByParts.push("is_latest:true");

  let sortBy: string | undefined;
  switch (request.sort) {
    case "title_asc":
      sortBy = "title:asc";
      break;
    case "title_desc":
      sortBy = "title:desc";
      break;
    case "author_asc":
      sortBy = "author:asc";
      break;
    case "author_desc":
      sortBy = "author:desc";
      break;
    case "source_order":
      sortBy = "source_order:asc";
      break;
    case "most_viewed":
      sortBy = "view_count:desc,source_order:asc";
      break;
    case "most_favorited":
      sortBy = "favorite_count:desc,source_order:asc";
      break;
  }

  return {
    q: query,
    queryBy,
    filterBy: filterByParts.length > 0 ? filterByParts.join(" && ") : undefined,
    sortBy,
  };
}
